import { ReactElement, ReactNode, useState } from 'react'
import {
  BottomSheet,
  Checkbox,
  DateTimeInput,
  MultiSelectField,
  salesReportIcons,
  SegmentedControl,
  SelectField,
} from '../../../components/SalesReport'
import { AccessReportFilterOptions, AccessReportGroupBy, CourtesyMode } from '../../../hooks/access-report'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import {
  AccessReportFilterState,
  activeFilterLabels,
  COURTESY_OPTIONS,
  DEFAULT_FILTERS,
  deviceOptions,
  GROUP_BY_OPTIONS,
  lotOptions,
  MAX_SELECTED_IDS,
  occasionOptions,
  ORIGIN_OPTIONS,
  periodError,
  resolvedOccasion,
  sectorOptions,
  ticketOptions,
  withSectors,
} from '../filters'
import * as S from './styles'

interface FiltersPanelProps {
  options: AccessReportFilterOptions
  value: AccessReportFilterState
  onChange: (next: AccessReportFilterState) => void
  onPeriodCommit?: (periodStart: string, periodEnd: string) => void
}

interface FieldsInput {
  options: AccessReportFilterOptions
  state: AccessReportFilterState
  change: (next: AccessReportFilterState) => void
  mobile: boolean
  onPeriodCommit?: () => void
}

interface Fields {
  start: ReactNode
  end: ReactNode
  error: string | null
  occasion: ReactNode
  sector: ReactNode
  ticket: ReactNode
  lot: ReactNode
  device: ReactNode
  origin: ReactNode
  groupBy: ReactNode
  courtesy: ReactNode
  checkboxes: ReactNode[]
}

const PERIOD_ERROR_ID = 'access-report-period-error'

const keepDisplay = (state: AccessReportFilterState): AccessReportFilterState => ({
  ...DEFAULT_FILTERS,
  occasion: state.occasion,
  groupBy: state.groupBy,
  courtesy: state.courtesy,
})

const buildFields = ({ options, state, change, mobile, onPeriodCommit }: FieldsInput): Fields => {
  const set = <K extends keyof AccessReportFilterState>(key: K, next: AccessReportFilterState[K]): void =>
    change({ ...state, [key]: next })
  const error = periodError(state)
  const multiSelect = { maxSelected: MAX_SELECTED_IDS, dense: mobile, autoFocusSearch: !mobile, inline: mobile }

  return {
    error,
    start: (
      <DateTimeInput
        id="access-report-period-start"
        label="Período — Início"
        value={state.periodStart}
        onChange={(next) => set('periodStart', next)}
        onCommit={onPeriodCommit}
        dense={mobile}
        invalid={error != null}
        describedBy={error != null ? PERIOD_ERROR_ID : undefined}
      />
    ),
    end: (
      <DateTimeInput
        id="access-report-period-end"
        label="Período — Fim"
        value={state.periodEnd}
        onChange={(next) => set('periodEnd', next)}
        onCommit={onPeriodCommit}
        dense={mobile}
        invalid={error != null}
        describedBy={error != null ? PERIOD_ERROR_ID : undefined}
      />
    ),
    occasion: (
      <SelectField
        id="access-report-occasion"
        label="Ocasião"
        value={resolvedOccasion(state, options)}
        options={occasionOptions(options)}
        onChange={(next) => set('occasion', next)}
        dense={mobile}
      />
    ),
    sector: (
      <MultiSelectField
        id="access-report-sector"
        label="Setor"
        value={state.sectorIds}
        options={sectorOptions(options)}
        onChange={(next) => change(withSectors(state, next, options))}
        {...multiSelect}
      />
    ),
    ticket: (
      <MultiSelectField
        id="access-report-ticket"
        label="Ingresso"
        value={state.ticketIds}
        options={ticketOptions(options, state.sectorIds)}
        onChange={(next) => set('ticketIds', next)}
        {...multiSelect}
      />
    ),
    lot: (
      <MultiSelectField
        id="access-report-lot"
        label="Lote"
        value={state.lotIds}
        options={lotOptions(options)}
        onChange={(next) => set('lotIds', next)}
        {...multiSelect}
      />
    ),
    device: (
      <MultiSelectField
        id="access-report-device"
        label="Dispositivo"
        value={state.deviceIds}
        options={deviceOptions(options)}
        onChange={(next) => set('deviceIds', next)}
        {...multiSelect}
      />
    ),
    origin: (
      <MultiSelectField
        id="access-report-origin"
        label="Origem da venda"
        value={state.origins}
        options={ORIGIN_OPTIONS}
        onChange={(next) => set('origins', next)}
        align={mobile ? 'start' : 'end'}
        {...multiSelect}
      />
    ),
    groupBy: (
      <SelectField
        id="access-report-group-by"
        label="Agrupar por"
        value={state.groupBy}
        options={GROUP_BY_OPTIONS}
        onChange={(next) => set('groupBy', next as AccessReportGroupBy)}
        inline={!mobile}
        dense={mobile}
        width={mobile ? undefined : '192px'}
      />
    ),
    courtesy: (
      <SegmentedControl
        ariaLabel="Cortesias"
        options={COURTESY_OPTIONS}
        value={state.courtesy}
        onChange={(next: CourtesyMode) => set('courtesy', next)}
      />
    ),
    checkboxes: [
      <Checkbox
        key="commissioners"
        label="Incluir comissários"
        checked={state.includeCommissioners}
        onChange={(next) => set('includeCommissioners', next)}
        dense={mobile}
      />,
      <Checkbox
        key="pending"
        label="Exibir pendentes"
        checked={state.showPending}
        onChange={(next) => set('showPending', next)}
        dense={mobile}
      />,
    ],
  }
}

function MobileFilters({ options, value, onChange, onPeriodCommit }: FiltersPanelProps): ReactElement {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<AccessReportFilterState>(value)
  const labels = activeFilterLabels(value, options)
  const fields = buildFields({ options, state: draft, change: setDraft, mobile: true })
  const display = buildFields({ options, state: value, change: onChange, mobile: true })

  const openSheet = (): void => {
    setDraft(value)
    setOpen(true)
  }
  const apply = (): void => {
    onChange(draft)
    onPeriodCommit?.(draft.periodStart, draft.periodEnd)
    setOpen(false)
  }

  return (
    <>
      <S.MobileBar>
        <S.FiltersButton
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={labels.length > 0 ? `Filtros, ${labels.length} ativos` : 'Filtros'}
          onClick={openSheet}
        >
          <img src={salesReportIcons.sliders} alt="" width={16} height={16} />
          {labels.length > 0 ? `Filtros · ${labels.length} ${labels.length === 1 ? 'ativo' : 'ativos'}` : 'Filtros'}
        </S.FiltersButton>
        <S.MobileSummary>
          <S.SummaryText>{labels.length > 0 ? labels.join(' · ') : 'Sem filtros aplicados'}</S.SummaryText>
        </S.MobileSummary>
      </S.MobileBar>
      <S.MobileDisplay aria-label="Agrupamento e cortesias">
        {display.groupBy}
        <S.MobileInline>
          <S.MobileLabel>Cortesias</S.MobileLabel>
          {display.courtesy}
        </S.MobileInline>
      </S.MobileDisplay>
      <BottomSheet
        open={open}
        title="Filtros"
        onClose={() => setOpen(false)}
        footer={
          <>
            <S.SheetButton type="button" onClick={() => setDraft(keepDisplay(draft))}>
              Limpar
            </S.SheetButton>
            <S.SheetButton type="button" $primary disabled={fields.error != null} onClick={apply}>
              Ver resultados
            </S.SheetButton>
          </>
        }
      >
        <S.SheetContent>
          <S.MobileStack $gap="12px">
            {fields.start}
            {fields.end}
            {fields.error && (
              <S.MobilePeriodError id={PERIOD_ERROR_ID} role="alert">
                {fields.error}
              </S.MobilePeriodError>
            )}
          </S.MobileStack>
          <S.MobileStack $gap="12px">
            {fields.occasion}
            {fields.sector}
            {fields.ticket}
            {fields.lot}
            {fields.device}
            {fields.origin}
          </S.MobileStack>
          <S.MobileStack $gap="12px">{fields.checkboxes}</S.MobileStack>
        </S.SheetContent>
      </BottomSheet>
    </>
  )
}

export function FiltersPanel(props: FiltersPanelProps): ReactElement {
  const mobile = useMediaQuery('(max-width: 767px)')
  if (mobile) return <MobileFilters {...props} />

  const { options, value, onChange, onPeriodCommit } = props
  const fields = buildFields({
    options,
    state: value,
    change: onChange,
    mobile: false,
    onPeriodCommit: () => onPeriodCommit?.(value.periodStart, value.periodEnd),
  })
  const clear = (): void => {
    onChange(keepDisplay(value))
    onPeriodCommit?.('', '')
  }

  return (
    <S.Card aria-label="Filtros">
      <S.FieldsGrid $padding="20px 24px 16px">
        {fields.start}
        {fields.end}
        {fields.occasion}
        {fields.sector}
      </S.FieldsGrid>
      {fields.error && (
        <S.PeriodError id={PERIOD_ERROR_ID} role="alert">
          {fields.error}
        </S.PeriodError>
      )}
      <S.FieldsGrid $padding="0 24px 20px">
        {fields.ticket}
        {fields.lot}
        {fields.device}
        {fields.origin}
      </S.FieldsGrid>
      <S.Strip>
        <S.StripRow>
          {fields.groupBy}
          <S.VerticalDivider aria-hidden="true" />
          <S.CourtesyGroup>
            <S.InlineLabel>Cortesias</S.InlineLabel>
            {fields.courtesy}
          </S.CourtesyGroup>
        </S.StripRow>
        <S.HorizontalDivider aria-hidden="true" />
        <S.StripRow>
          {fields.checkboxes.map((checkbox, index) => (
            <S.CheckboxCell key={index}>{checkbox}</S.CheckboxCell>
          ))}
          <S.ClearLink type="button" onClick={clear}>
            Limpar filtros
          </S.ClearLink>
        </S.StripRow>
      </S.Strip>
    </S.Card>
  )
}
